// Client/src/ValueBreakdown.jsx
// v4 – Factor breakdown with deep, case-specific explanations per factor
// Uses factor_scores + factor_explanations from evaluatorV2 and keeps the bar layout.

import React from "react";
import { getReportLabels } from "./reportI18n";

function clampScore(value) {
  const v = Number(value ?? 0);
  if (Number.isNaN(v)) return 0;
  return Math.min(10, Math.max(0, v));
}

function formatScoreInt(value) {
  return String(Math.round(clampScore(value)));
}

function formatNetInt(value) {
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : "—";
}

function localizedFactorName(key, code = "en") {
  const normalized = String(key || "").toLowerCase().trim();
  const map = {
    da: {
      "new opportunities": "nye muligheder",
      "cost savings": "omkostningsbesparelser",
      "risk reduction": "risikoreduktion",
      "tokenization costs": "tokeniseringsomkostninger",
      "new risks": "nye risici",
    },
    de: {
      "new opportunities": "neue Chancen",
      "cost savings": "Kosteneinsparungen",
      "risk reduction": "Risikominderung",
      "tokenization costs": "Tokenisierungskosten",
      "new risks": "neue Risiken",
    },
    fr: {
      "new opportunities": "nouvelles opportunités",
      "cost savings": "économies de coûts",
      "risk reduction": "réduction des risques",
      "tokenization costs": "coûts de tokenisation",
      "new risks": "nouveaux risques",
    },
    es: {
      "new opportunities": "nuevas oportunidades",
      "cost savings": "ahorro de costes",
      "risk reduction": "reducción de riesgos",
      "tokenization costs": "costes de tokenización",
      "new risks": "nuevos riesgos",
    },
    "zh-Hans": {
      "new opportunities": "新机会",
      "cost savings": "成本节约",
      "risk reduction": "风险降低",
      "tokenization costs": "代币化成本",
      "new risks": "新风险",
    },
    "pt-BR": {
      "new opportunities": "novas oportunidades",
      "cost savings": "economia de custos",
      "risk reduction": "redução de riscos",
      "tokenization costs": "custos de tokenização",
      "new risks": "novos riscos",
    },
  };
  return map[code]?.[normalized] || key;
}

function buildLocalizedMicroSummary(verdict, plusTotal, minusTotal, addedScore, scores, labels) {
  const code = labels?.languageCode || "en";
  if (code === "en") return null;

  const verdictKey = String(verdict || "").toLowerCase().trim();
  const nonViableSet = new Set(["no-go", "no go", "non-viable", "non viable", "stop", "non_viable"]);
  const conditionalSet = new Set(["conditional_go", "conditional go", "conditional"]);
  const viableSet = new Set(["viable", "go", "go / viable"]);
  const isNonViable = nonViableSet.has(verdictKey);
  const isConditional = conditionalSet.has(verdictKey);
  const isViable = viableSet.has(verdictKey);

  const av = Number.isFinite(addedScore) && typeof addedScore === "number" ? Number(addedScore) : null;
  const no = clampScore(scores.new_opportunities);
  const cs = clampScore(scores.cost_savings);
  const rr = clampScore(scores.risk_reduction);
  const tc = clampScore(scores.tokenization_costs);
  const nr = clampScore(scores.new_risks);

  const plusFactors = [
    { key: "New opportunities", score: no },
    { key: "Cost savings", score: cs },
    { key: "Risk reduction", score: rr },
  ];
  const minusFactors = [
    { key: "Tokenization costs", score: tc },
    { key: "New risks", score: nr },
  ];
  const topPlus = plusFactors.reduce((best, cur) => (cur.score > best.score ? cur : best), plusFactors[0]);
  const topMinus = minusFactors.reduce((best, cur) => (cur.score > best.score ? cur : best), minusFactors[0]);
  const topPlusName = localizedFactorName(topPlus.key, code);
  const topMinusName = localizedFactorName(topMinus.key, code);

  const base = {
    da: isNonViable
      ? "Samlet set er det nuværende design strukturelt negativt: tokenisering tilfører mere omkostning og risiko, end den skaber i robust upside, så casen bør behandles som Non-viable, medmindre kernestrukturen redesignes væsentligt."
      : isViable
      ? (av !== null && av >= 7
        ? "Samlet set skaber denne struktur en tydeligt positiv balance: stærk upside fra nye muligheder og/eller risikoreduktion overstiger tokeniseringsomkostninger og nye risici, forudsat solid governance og implementeringsdisciplin."
        : "Samlet set skaber denne struktur en positiv value case, men resultatet afhænger stadig af disciplineret eksekvering, scope-kontrol og undgåelse af unødig friktion under implementering.")
      : isConditional
      ? "Samlet set er casen fint balanceret, hvor modelleret upside og modelleret friktion ligger tæt på hinanden. Strukturen er retningsmæssigt mulig, men konkrete designbetingelser og risikoreduktioner skal være på plads før fuld udrulning."
      : "Samlet set viser merværdiscoren nettobalancen mellem positive faktorer og friktioner i netop dette design.",
    de: isNonViable
      ? "Insgesamt ist das aktuelle Design strukturell negativ: Tokenisierung bringt mehr Kosten und Risiken ein, als sie belastbares Upside schafft; der Fall sollte daher als Non-viable behandelt werden, sofern die Kernstruktur nicht wesentlich neu gestaltet wird."
      : isViable
      ? (av !== null && av >= 7
        ? "Insgesamt schafft diese Struktur eine klar positive Balance: starkes Upside aus neuen Chancen und/oder Risikominderung überwiegt Tokenisierungskosten und neue Risiken, sofern Governance und Umsetzungsdisziplin solide bleiben."
        : "Insgesamt schafft diese Struktur einen positiven Value Case, aber das Ergebnis hängt weiterhin von disziplinierter Umsetzung, Scope-Kontrolle und der Vermeidung unnötiger Reibung in der Implementierung ab.")
      : isConditional
      ? "Insgesamt ist der Fall fein austariert, wobei modelliertes Upside und modellierte Reibung nahe beieinander liegen. Die Struktur ist richtungsweise möglich, aber konkrete Designbedingungen und Risikominderungen müssen vor einem vollständigen Rollout erfüllt werden."
      : "Insgesamt zeigt der Mehrwert-Score die Nettobilanz zwischen positiven Faktoren und Reibungen in genau diesem Design.",
    fr: isNonViable
      ? "Globalement, le design actuel est structurellement négatif : la tokenisation introduit davantage de coûts et de risques qu’elle ne crée d’upside durable ; le cas doit donc être traité comme Non-viable sauf refonte substantielle de la structure centrale."
      : isViable
      ? (av !== null && av >= 7
        ? "Globalement, cette structure crée un équilibre clairement positif : un upside fort provenant des nouvelles opportunités et/ou de la réduction des risques dépasse les coûts de tokenisation et les nouveaux risques, sous réserve d’une gouvernance et d’une discipline d’exécution solides."
        : "Globalement, cette structure crée un cas de valeur positif, mais le résultat dépend encore d’une exécution disciplinée, du contrôle du périmètre et de l’évitement de frictions inutiles pendant la mise en œuvre.")
      : isConditional
      ? "Globalement, le cas est finement équilibré, avec un upside modélisé et des frictions modélisées proches en magnitude. La structure est globalement plausible, mais des conditions de conception et des mesures d’atténuation spécifiques doivent être satisfaites avant un déploiement complet."
      : "Globalement, le score de valeur ajoutée reflète l’équilibre net entre facteurs positifs et frictions dans ce design précis.",
    es: isNonViable
      ? "En conjunto, el diseño actual es estructuralmente negativo: la tokenización introduce más coste y riesgo de lo que crea como upside duradero, por lo que el caso debe tratarse como Non-viable salvo que la estructura central se rediseñe materialmente."
      : isViable
      ? (av !== null && av >= 7
        ? "En conjunto, esta estructura crea un balance claramente positivo: un upside fuerte procedente de nuevas oportunidades y/o reducción de riesgos supera los costes de tokenización y los nuevos riesgos, siempre que se ejecute con buena gobernanza y disciplina de implementación."
        : "En conjunto, esta estructura crea un caso de valor positivo, pero el resultado aún depende de ejecución disciplinada, control de alcance y evitar fricción innecesaria durante la implementación.")
      : isConditional
      ? "En conjunto, el caso está finamente equilibrado, con upside modelado y fricción modelada de magnitud similar. La estructura es plausible en términos generales, pero deben cumplirse condiciones de diseño y mitigaciones concretas antes de comprometerse con un despliegue completo."
      : "En conjunto, la puntuación de valor añadido refleja el balance neto entre factores positivos y fricciones en este diseño específico.",
    "zh-Hans": isNonViable
      ? "总体而言，当前设计在结构上为负：代币化引入的成本和风险大于其创造的可持续上行价值，因此除非核心结构被实质性重构，否则应将本案例视为Non-viable。"
      : isViable
      ? (av !== null && av >= 7
        ? "总体而言，该结构形成了明确的正向平衡：来自新机会和/或风险降低的强上行价值超过代币化成本和新风险，前提是治理和实施纪律足够稳健。"
        : "总体而言，该结构形成了正向价值案例，但结果仍取决于有纪律的执行、范围控制以及在实施中避免不必要摩擦。")
      : isConditional
      ? "总体而言，本案例处于精细平衡状态，模型化上行价值与模型化摩擦规模接近。该结构总体上具有可行性，但在进入全面推出前，需要满足具体设计条件并落实缓释措施。"
      : "总体而言，增值评分反映了该具体设计中正向因素与摩擦因素之间的净平衡。",
    "pt-BR": isNonViable
      ? "No conjunto, o desenho atual é estruturalmente negativo: a tokenização introduz mais custos e riscos do que gera benefícios sustentáveis, de modo que o caso deve ser tratado como Non-viable, a menos que a estrutura central seja redesenhada de forma substancial."
      : isViable
      ? (av !== null && av >= 7
        ? "No conjunto, esta estrutura apresenta um equilíbrio claramente positivo: os benefícios decorrentes de novas oportunidades e/ou da redução de riscos superam os custos de tokenização e os novos riscos, desde que haja governança sólida e disciplina na implementação."
        : "No conjunto, esta estrutura apresenta uma proposta de valor positiva, mas o resultado ainda depende de execução disciplinada, controle de escopo e prevenção de fricções desnecessárias durante a implementação.")
      : isConditional
      ? "No conjunto, o caso está finamente equilibrado, com benefícios e fricções modelados em magnitudes próximas. A estrutura pode ser viável em princípio, mas condições específicas de desenho e medidas de mitigação precisam ser atendidas antes de um lançamento em escala plena."
      : "No conjunto, a pontuação de Valor Agregado reflete o saldo líquido entre fatores positivos e fricções neste desenho específico.",
  }[code];

  let context = "";
  if (av !== null) {
    if (code === "zh-Hans") {
      context = ` 增值评分为${formatNetInt(av)}。净增值主要由上行侧的${topPlusName}（${formatScoreInt(topPlus.score)}/10）驱动，同时主要受摩擦侧的${topMinusName}（${formatScoreInt(topMinus.score)}/10）制约。`;
    } else if (code === "da") {
      context = ` Med en merværdiscore på ${formatNetInt(av)} bør resultatet læses sammen med de konkrete faktorer nedenfor. Nettomerværdi drives især af ${topPlusName} på ${formatScoreInt(topPlus.score)}/10 på upside-siden og begrænses især af ${topMinusName} på ${formatScoreInt(topMinus.score)}/10 på friktionssiden.`;
    } else if (code === "de") {
      context = ` Mit einem Mehrwert-Score von ${formatNetInt(av)} sollte das Ergebnis zusammen mit den konkreten Faktoren unten gelesen werden. Der Netto-Mehrwert wird vor allem durch ${topPlusName} mit ${formatScoreInt(topPlus.score)}/10 auf der Upside-Seite getrieben und hauptsächlich durch ${topMinusName} mit ${formatScoreInt(topMinus.score)}/10 auf der Reibungsseite begrenzt.`;
    } else if (code === "fr") {
      context = ` Avec un score de valeur ajoutée de ${formatNetInt(av)}, le résultat doit être lu avec les facteurs concrets ci-dessous. La valeur ajoutée nette est principalement portée par ${topPlusName} à ${formatScoreInt(topPlus.score)}/10 côté upside, et limitée surtout par ${topMinusName} à ${formatScoreInt(topMinus.score)}/10 côté friction.`;
    } else if (code === "es") {
      context = ` Con una puntuación de valor añadido de ${formatNetInt(av)}, el resultado debe leerse junto con los factores concretos siguientes. El valor añadido neto está impulsado principalmente por ${topPlusName} con ${formatScoreInt(topPlus.score)}/10 en el lado positivo, y limitado sobre todo por ${topMinusName} con ${formatScoreInt(topMinus.score)}/10 en el lado de fricción.`;
    } else if (code === "pt-BR") {
      context = ` Com uma pontuação de Valor Agregado de ${formatNetInt(av)}, o resultado deve ser lido em conjunto com os fatores concretos abaixo. O Valor Agregado líquido é impulsionado principalmente por ${topPlusName}, com ${formatScoreInt(topPlus.score)}/10 no lado positivo, e limitado sobretudo por ${topMinusName}, com ${formatScoreInt(topMinus.score)}/10 no lado das fricções.`;
    }
  }

  return `${base}${context}`;
}


function describeScore(kind, score, labels = null) {
  const v = clampScore(score);

  if (kind === "plus") {
    if (labels && labels.languageCode !== "en") {
      if (v >= 9) return labels.veryHigh;
      if (v >= 6) return labels.high;
      if (v >= 4) return labels.medium;
      if (v >= 2) return labels.low;
      return labels.low;
    }
    if (v >= 9) return "very strong upside";
    if (v >= 7.5) return "strong upside";
    if (v >= 6) return "solid upside";
    if (v >= 4) return "moderate upside";
    if (v >= 2) return "limited upside";
    return "very limited upside";
  } else {
    // downside factors – Tokenization costs / New risks
    if (labels && labels.languageCode !== "en") {
      if (v >= 9) return labels.veryHigh;
      if (v >= 6) return labels.high;
      if (v >= 4) return labels.medium;
      if (v >= 2) return labels.low;
      return labels.low;
    }
    if (v >= 9) return "very severe friction";
    if (v >= 7.5) return "high friction";
    if (v >= 6) return "material friction";
    if (v >= 4) return "moderate friction";
    if (v >= 2) return "modest friction";
    return "very limited friction";
  }
}

function FactorRow({ label, score, kind, labels }) {
  const v = clampScore(score);
  const pct = (v / 10) * 100;

  // Positive factors (NO, CS, RR) are upside (green), TC/NR are downside (red)
  const isUpside = kind === "plus";
  const barColor = isUpside ? "#2f855a" : "#c53030";
  const backgroundColor = "#edf2f7";
  const descriptor = describeScore(kind, v, labels);

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
          gap: 8,
        }}
      >
        <span>{label}</span>
        <span className="muted small" style={{ textAlign: "right" }}>
          {labels?.scoreOutOfTen ? labels.scoreOutOfTen(formatScoreInt(v), descriptor) : `${formatScoreInt(v)} / 10 (${descriptor})`}
        </span>
      </div>
      <div
        style={{
          position: "relative",
          height: 8,
          borderRadius: 4,
          backgroundColor,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            backgroundColor: barColor,
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}

function ExplanationBlock({ title, bullets, fallback, note }) {
  const items = Array.isArray(bullets) ? bullets.filter(Boolean) : [];

  return (
    <div className="factor-explanation-block" style={{ marginBottom: 24 }}>
      <h4
  className="factor-explanation-title"
  style={{
    marginTop: 16,
    marginBottom: 8,
    fontWeight: 800,
    lineHeight: 1.25,
    color: "#111",
  }}
>
  {title}
</h4>
      {items.length > 0 ? (
        <ul>
          {items.map((line, idx) => (
            <li key={idx}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className="muted small">
          {fallback ||
            "No detailed explanation was provided by the evaluator for this factor in this run."}
        </p>
      )}
      {note && (
        <p className="muted small" style={{ marginTop: 6 }}>
          {note}
        </p>
      )}
    </div>
  );
}

function buildMicroSummary(verdict, plusTotal, minusTotal, addedScore, scores, labels = getReportLabels("en")) {
  const verdictKey = String(verdict || "").toLowerCase().trim();

  const nonViableSet = new Set([
    "no-go",
    "no go",
    "non-viable",
    "non viable",
    "stop",
    "non_viable",
  ]);
  const conditionalSet = new Set([
    "conditional_go",
    "conditional go",
    "conditional",
  ]);
  const viableSet = new Set(["viable", "go", "go / viable"]);

  const isNonViable = nonViableSet.has(verdictKey);
  const isConditional = conditionalSet.has(verdictKey);
  const isViable = viableSet.has(verdictKey);

  const av =
    Number.isFinite(addedScore) && typeof addedScore === "number"
      ? Number(addedScore)
      : null;

  const no = clampScore(scores.new_opportunities);
  const cs = clampScore(scores.cost_savings);
  const rr = clampScore(scores.risk_reduction);
  const tc = clampScore(scores.tokenization_costs);
  const nr = clampScore(scores.new_risks);

  const plusFactors = [
    { key: "New opportunities", score: no },
    { key: "Cost savings", score: cs },
    { key: "Risk reduction", score: rr },
  ];
  const minusFactors = [
    { key: "Tokenization costs", score: tc },
    { key: "New risks", score: nr },
  ];

  const topPlus = plusFactors.reduce(
    (best, cur) => (cur.score > best.score ? cur : best),
    plusFactors[0]
  );
  const topMinus = minusFactors.reduce(
    (best, cur) => (cur.score > best.score ? cur : best),
    minusFactors[0]
  );

  let baseText = "";
  let contextText = "";

  if (labels.languageCode !== "en") {
    if (isNonViable) {
      baseText = labels.valueBreakdownOverallNonViable || "";
    } else if (isViable) {
      baseText = av !== null && av >= 7
        ? labels.valueBreakdownOverallViableStrong || ""
        : labels.valueBreakdownOverallViable || "";
    } else if (isConditional) {
      baseText = labels.valueBreakdownOverallConditional || "";
    } else {
      baseText = labels.valueBreakdownOverallGeneric || "";
    }
  } else if (isNonViable) {
    baseText =
      av !== null && av > -4
        ? "The Non-viable verdict is driven by an overriding blocker rather than the score band. Read the identified blocker separately from the aggregate balance of benefits, costs and risks."
        : "Overall, the negative aggregate score supports Non-viable under the stated assumptions; reassess only after addressing the case-specific value shortfall.";
  } else if (isViable) {
    baseText =
      `The factor profile shows a positive tokenization value case. The strongest upside comes from ${topPlus.key.toLowerCase()}, while the principal friction is ${topMinus.key.toLowerCase()}; the explanations below set out the case-specific mechanisms and evidence gaps behind those judgments.`;
  } else if (isConditional) {
    baseText =
      "Conditional Go identifies a design-dependent assessment, not a positive implementation decision. Where the aggregate score is negative, the current design has not established net tokenization value. Further work must demonstrate incremental benefits or lower costs and risks against the stated conventional comparator before a rollout is justified.";
  } else {
    baseText =
      "Overall, the Added Value score reflects the net balance between upside factors (New opportunities, Cost savings, Risk reduction) and frictions (Tokenization costs and New risks) for this specific design. Use the factor breakdown and recommendations below to see where the structure can be improved.";
  }

  if (av !== null && labels.languageCode === "en") {
    if (isNonViable) {
      contextText =
        av <= -6
          ? ` With an Added Value score of ${formatNetInt(av)}, this is not a narrow decision-range case. The gap is large enough that incremental tuning is unlikely to restore viability on its own.`
          : av <= -4
          ? ` With an Added Value score of ${formatNetInt(av)}, the case falls in the Non-viable score band.`
          : ` The Added Value score is ${formatNetInt(av)}; this score alone would not support Non-viable. The overriding blocker must be resolved before reconsidering the verdict.`;
    } else if (isConditional) {
      contextText =
        Math.abs(av) <= 2
          ? ` With an Added Value score of ${formatNetInt(av)}, this case sits near the decision boundary, so a limited set of design choices can still shift the outcome.`
          : ` With an Added Value score of ${formatNetInt(av)}, the case remains condition-sensitive and should be judged by whether the key gating conditions can be locked in.`;
    } else if (isViable) {
      contextText =
        av >= 7
          ? ` With an Added Value score of ${formatNetInt(av)}, the value case is clearly positive rather than marginal.`
          : ` With an Added Value score of ${formatNetInt(av)}, the case is positive but still execution-sensitive.`;
    } else {
      contextText =
        ` With an Added Value score of ${formatNetInt(av)}, the net balance should be read together with the factor profile below.`;
    }

    const driverText =
      isViable
        ? ""
        : " Net Added Value is primarily driven by " +
          `${topPlus.key.toLowerCase()} at ${formatScoreInt(topPlus.score)}/10 on the upside, and constrained mainly by ` +
          `${topMinus.key.toLowerCase()} at ${formatScoreInt(topMinus.score)}/10 on the friction side.`;

    contextText += driverText;
  }

  return baseText + contextText;
}



export default function ValueBreakdown(props) {
  // Try a few prop names so vi ikke bryder eksisterende brug
  const result =
    props.result || props.data || props.response || props.analysis || {};
  const labels = getReportLabels(props.reportLanguageCode || props.lang || props.language || "en");
  const factorReadingIntro = {
    en: "This chapter explains the causal basis of the five scores: which facts, assumptions and evidence gaps create upside, and which ones introduce additional friction. The three positive factors ask what tokenization adds relative to a realistic conventional alternative; the two negative factors ask what extra cost or risk the token layer introduces. A low positive score therefore means limited or weakly evidenced tokenization benefit, while a low negative score means limited additional friction. The scores are calibrated methodology judgments, not measured outcomes or legal conclusions.",
    da: "Dette kapitel forklarer årsagen til de fem scorer: hvilke konkrete forhold, antagelser og evidenshuller der skaber potentiale, og hvilke der introducerer ekstra friktion. De tre positive faktorer spørger, hvad tokenisering tilfører i forhold til et realistisk konventionelt alternativ; de to negative faktorer spørger, hvilke ekstra omkostninger eller risici tokenlaget selv skaber. En lav positiv score betyder derfor begrænset eller svagt dokumenteret tokeniseringsgevinst, mens en lav negativ score betyder begrænset ekstra friktion. Scorerne er kalibrerede metodevurderinger — ikke målte resultater eller juridiske konklusioner.",
    de: "Dieses Kapitel erklärt die kausale Grundlage der fünf Scores: welche konkreten Fakten, Annahmen und Evidenzlücken Potenzial schaffen und welche zusätzliche Reibung erzeugen. Die drei positiven Faktoren fragen, was Tokenisierung gegenüber einer realistischen konventionellen Alternative zusätzlich bringt; die beiden negativen Faktoren fragen, welche zusätzlichen Kosten oder Risiken die Token-Ebene selbst einführt. Ein niedriger positiver Score bedeutet daher begrenzten oder schwach belegten Tokenisierungsnutzen, während ein niedriger negativer Score geringe zusätzliche Reibung bedeutet. Die Scores sind kalibrierte Methodenurteile, keine gemessenen Ergebnisse oder Rechtsaussagen.",
    fr: "Ce chapitre explique la base causale des cinq scores : quels faits, hypothèses et manques de preuve créent le potentiel, et lesquels introduisent des frictions supplémentaires. Les trois facteurs positifs demandent ce que la tokenisation ajoute par rapport à une alternative conventionnelle réaliste ; les deux facteurs négatifs mesurent les coûts ou risques additionnels introduits par la couche token. Un score positif faible signifie donc un bénéfice de tokenisation limité ou peu étayé, tandis qu'un score négatif faible signifie peu de friction supplémentaire. Les scores sont des jugements méthodologiques calibrés, et non des résultats mesurés ou des conclusions juridiques.",
    es: "Este capítulo explica la base causal de las cinco puntuaciones: qué hechos, supuestos y lagunas de evidencia generan potencial y cuáles introducen fricción adicional. Los tres factores positivos preguntan qué añade la tokenización frente a una alternativa convencional realista; los dos factores negativos preguntan qué costes o riesgos adicionales introduce la propia capa token. Por tanto, una puntuación positiva baja significa un beneficio de tokenización limitado o débilmente demostrado, mientras que una puntuación negativa baja significa poca fricción adicional. Las puntuaciones son juicios metodológicos calibrados, no resultados medidos ni conclusiones jurídicas.",
    "zh-Hans": "本章解释五项评分的因果基础：哪些具体事实、假设和证据缺口形成上行价值，哪些因素又引入额外摩擦。三个正向因素衡量代币化相对于现实可行的传统替代方案究竟增加了什么；两个负向因素衡量代币层本身新增了哪些成本或风险。因此，较低的正向评分表示代币化收益有限或证据不足，而较低的负向评分表示新增摩擦较小。所有评分均为经过校准的方法论判断，并非实测结果或法律结论。",
    "pt-BR": "Este capítulo explica a base causal das cinco pontuações: quais fatos, premissas e lacunas de evidência criam potencial e quais introduzem fricção adicional. Os três fatores positivos perguntam o que a tokenização acrescenta em relação a uma alternativa convencional realista; os dois fatores negativos perguntam quais custos ou riscos adicionais a própria camada de token introduz. Assim, uma pontuação positiva baixa significa benefício de tokenização limitado ou pouco comprovado, enquanto uma pontuação negativa baixa significa pouca fricção adicional. As pontuações são julgamentos metodológicos calibrados, não resultados medidos nem conclusões jurídicas.",
  }[labels.languageCode] || "This chapter explains the causal basis of the five scores: which facts, assumptions and evidence gaps create upside, and which ones introduce additional friction. The scores are calibrated methodology judgments, not measured outcomes or legal conclusions.";

  const scores = result.factor_scores || {};
  const sourceFx = result.factor_explanations || {};
  const fx = Object.fromEntries([...new Set([...Object.keys(sourceFx), ...Object.keys(result?.case_decision_frame?.factor_baselines || {})])].map((key) => { const lines = sourceFx[key]; return [key,
    result?.case_decision_frame?.factor_baselines?.[key]
      ? [result.case_decision_frame.factor_baselines[key], ...(Array.isArray(lines) ? lines : lines ? [String(lines)] : [])]
      : lines
  ]; }));
  const av = result.added_value || {};

  const plusTotal = Number(av.plus ?? 0);
  const minusTotal = Number(av.minus ?? 0);
  const addedScore = Number(
    Number.isFinite(av.score) ? av.score : plusTotal - minusTotal
  );

  const rows = [
    {
      key: "new_opportunities",
      label: labels.newOpportunities,
      kind: "plus",
    },
    {
      key: "cost_savings",
      label: labels.costSavings,
      kind: "plus",
    },
    {
      key: "risk_reduction",
      label: labels.riskReduction,
      kind: "plus",
    },
    {
      key: "tokenization_costs",
      label: labels.tokenizationCosts,
      kind: "minus",
    },
    {
      key: "new_risks",
      label: labels.newRisks,
      kind: "minus",
    },
  ];

  const microSummary = labels.languageCode === "en"
    ? buildMicroSummary(result.verdict, plusTotal, minusTotal, addedScore, scores, labels)
    : buildLocalizedMicroSummary(result.verdict, plusTotal, minusTotal, addedScore, scores, labels);

  const tcScore = clampScore(scores.tokenization_costs);
  const nrScore = clampScore(scores.new_risks);

  return (
    <section style={{ marginTop: 12 }}>
      <p style={{ maxWidth: 760, marginTop: 0 }}>
        {factorReadingIntro}
      </p>

      <div className="factor-formula-reminder" aria-label="Added Value formula">
        <strong>{labels.formulaText || "Added Value = (New Opportunities + Cost Savings + Risk Reduction) – (Tokenization Costs + New Risks)"}</strong>
      </div>

      {microSummary && (
        <p
          className="muted"
          style={{ maxWidth: 760, marginTop: 8, fontStyle: "italic" }}
        >
          {microSummary}
        </p>
      )}

      <div
        className="value-breakdown-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.2fr)",
          gap: 32,
          marginTop: 24,
        }}
      >
        {/* Left column: textual explanations per factor */}
        <div className="value-breakdown-explanations">
          <ExplanationBlock
            title={`${labels.newOpportunities} (NO)`}
            bullets={fx.new_opportunities}
            fallback={labels.noFactorExplanation}
            />
          <ExplanationBlock
            title={`${labels.costSavings} (CS)`}
            bullets={fx.cost_savings}
            fallback={labels.noFactorExplanation}
          />
          <ExplanationBlock
            title={`${labels.riskReduction} (RR)`}
            bullets={fx.risk_reduction}
            fallback={labels.noFactorExplanation}
          />
          <ExplanationBlock
            title={`${labels.tokenizationCosts} (TC)`}
            bullets={fx.tokenization_costs}
            fallback={labels.noFactorExplanation}
          />
          <ExplanationBlock
            title={`${labels.newRisks} (NR)`}
            bullets={fx.new_risks}
            fallback={labels.noFactorExplanation}
          />
        </div>

        {/* Right column: numeric scores + bars + plus/minus */}
        <div className="value-breakdown-scores">
          {rows.map((row) => (
            <FactorRow
              key={row.key}
              label={row.label}
              score={scores[row.key]}
              kind={row.kind}
              labels={labels}
            />
          ))}

          <div style={{ marginTop: 16 }}>
            <p>
              <strong>{labels.plusFormula}:</strong>{" "}
              {formatNetInt(plusTotal)}
            </p>
            <p>
              <strong>{labels.minusFormula}:</strong>{" "}
              {formatNetInt(minusTotal)}
            </p>
            <p>
              <strong>{labels.addedValueFormula}:</strong>{" "}
              {formatNetInt(addedScore)}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
